import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Lock, Mail, User } from "lucide-react";
import { useAuth, formatErr } from "../lib/auth";
import { notify } from "../lib/notify";

export function Login() {
  const { login, googleStart } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    const fd = new FormData(e.currentTarget);
    try {
      await login(fd.get("email"), fd.get("password"));
      notify.success("Welcome back to RiskPedia SG.");
      const next = new URLSearchParams(loc.search).get("next") || "/profile";
      nav(next);
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="page-login">
      <AuthShell title="Welcome back." kicker="Sign in to RiskPedia SG">
        <button type="button" onClick={googleStart} className="w-full flex items-center justify-center gap-3 border border-slate-200 rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-slate-50 transition-colors" data-testid="login-google">
          <GoogleIcon /> Continue with Google
        </button>
        <div className="relative my-5"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div><div className="relative flex justify-center"><span className="px-3 text-xs font-mono text-slate-400 bg-white" style={{ background: "var(--rp-card-bg)" }}>OR EMAIL</span></div></div>
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <Field icon={<Mail size={14} />} name="email" type="email" placeholder="work-email@bank.sg" required tid="login-email" />
          <Field icon={<Lock size={14} />} name="password" type="password" placeholder="Password" required tid="login-password" />
          {err && <div className="text-xs text-rose-600" data-testid="login-error">{err}</div>}
          <button disabled={busy} className="btn-accent w-full justify-center" data-testid="login-submit">{busy ? "Signing in…" : "Sign in"} <ArrowRight size={14} /></button>
          <div className="text-xs text-slate-500 text-center">No account? <Link to="/signup" className="font-medium" style={{ color: "var(--rp-accent)" }}>Create one</Link></div>
        </form>
      </AuthShell>
    </div>
  );
}

export function Signup() {
  const { register, googleStart } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    const fd = new FormData(e.currentTarget);
    try {
      await register(fd.get("email"), fd.get("password"), fd.get("name"));
      notify.success("Account created. Welcome to RiskPedia SG.");
      nav("/profile");
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="page-signup">
      <AuthShell title="Create your account." kicker="Free — no credit card">
        <button type="button" onClick={googleStart} className="w-full flex items-center justify-center gap-3 border border-slate-200 rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-slate-50 transition-colors" data-testid="signup-google">
          <GoogleIcon /> Sign up with Google
        </button>
        <div className="relative my-5"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div><div className="relative flex justify-center"><span className="px-3 text-xs font-mono text-slate-400" style={{ background: "var(--rp-card-bg)" }}>OR EMAIL</span></div></div>
        <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
          <Field icon={<User size={14} />} name="name" type="text" placeholder="Your name" tid="signup-name" />
          <Field icon={<Mail size={14} />} name="email" type="email" placeholder="work-email@bank.sg" required tid="signup-email" />
          <Field icon={<Lock size={14} />} name="password" type="password" placeholder="Password (min 8 chars)" required tid="signup-password" />
          {err && <div className="text-xs text-rose-600" data-testid="signup-error">{err}</div>}
          <button disabled={busy} className="btn-accent w-full justify-center" data-testid="signup-submit">{busy ? "Creating…" : "Create account"} <ArrowRight size={14} /></button>
          <div className="text-xs text-slate-500 text-center">Already have one? <Link to="/login" className="font-medium" style={{ color: "var(--rp-accent)" }}>Sign in</Link></div>
        </form>
      </AuthShell>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.614z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
      <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  );
}

export function GoogleAuthCallback() {
  const { googleSession } = useAuth();
  const nav = useNavigate();
  const [err, setErr] = useState("");

  useEffect(() => {
    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sid = params.get("session_id");
    if (!sid) { setErr("No session_id in callback URL"); return; }
    googleSession(sid)
      .then(() => { notify.success("Signed in with Google."); nav("/profile", { replace: true }); })
      .catch((e) => { setErr(formatErr(e.response?.data?.detail) || e.message); });
  }, []); // eslint-disable-line

  return (
    <section className="section">
      <div className="container-rp max-w-lg text-center" data-testid="page-google-callback">
        {!err ? <>
          <div className="overline">Signing you in</div>
          <h1 className="font-display text-3xl font-semibold mt-3">One moment…</h1>
          <p className="text-slate-500 mt-3">Completing the Google sign-in handshake.</p>
        </> : <>
          <div className="overline">Sign-in failed</div>
          <h1 className="font-display text-3xl font-semibold mt-3">{err}</h1>
          <Link to="/login" className="btn-outline mt-6">Try again</Link>
        </>}
      </div>
    </section>
  );
}

function AuthShell({ title, kicker, children }) {
  return (
    <section className="section">
      <div className="container-rp max-w-md">
        <div className="overline">{kicker}</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold mt-3 tracking-tight">{title}</h1>
        <div className="rp-card p-8 mt-8">{children}</div>
        <p className="text-xs text-slate-500 mt-6 leading-relaxed">By signing up you agree that RiskPedia SG content is educational only and not legal advice. We store your email per the PDPA — you can delete your account anytime.</p>
      </div>
    </section>
  );
}

function Field({ icon, tid, ...rest }) {
  return (
    <label className="flex items-center gap-3 border border-slate-200 rounded-sm px-3 py-2 focus-within:border-slate-400">
      <span className="text-slate-400">{icon}</span>
      <input className="flex-1 bg-transparent text-sm focus:outline-none border-0" data-testid={tid} {...rest} />
    </label>
  );
}

export function ProtectedRoute({ children, admin }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="container-rp py-24 text-slate-400">Loading…</div>;
  if (!user) {
    const next = encodeURIComponent(window.location.pathname);
    return <RedirectTo to={`/login?next=${next}`} />;
  }
  if (admin && user.role !== "admin") return <RedirectTo to="/profile" />;
  return children;
}

function RedirectTo({ to }) {
  const nav = useNavigate();
  React.useEffect(() => { nav(to, { replace: true }); }, [to, nav]);
  return null;
}
